import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface ContextMenuPosition {
  x: number;
  y: number;
}

interface ContextMenuProps {
  position: ContextMenuPosition;
  onClose: () => void;
  children: ReactNode;
}

/**
 * Malý ukotvený popover pro kontextové akce na canvasu (tabulka, sloupec,
 * skupina, poznámka, prázdná plocha) — na rozdíl od centrovaného `Modal`
 * se otevírá u kurzoru a zavírá kliknutím mimo nebo Escape.
 */
export function ContextMenu({ position, onClose, children }: ContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // Portál do `document.body` (ne obyčejný render na místě) — React Flow
  // `<Panel>` (odkud se otevírá např. `ViewSettingsPanel`, Epic 8) je
  // `position: absolute` s vlastním `z-index`, což z něj dělá NOVÝ stacking
  // context. `position: fixed` sice díky tomu pořád vizuálně kotví vůči
  // viewportu (nic v řetězci předků nenastavuje transform/filter/contain,
  // což by ho jako jediné mohlo přesměrovat), ale pro pořadí PŘEKRESLENÍ
  // zůstává potomkem stacking contextu `<Panel>` — `z-index: 1000` na tomhle
  // divu by tak porovnávalo jen se sourozenci UVNITŘ `<Panel>`, ne s
  // `.inspector` panelem úplně jinde ve stromu, který by menu i tak
  // překryl a zabral kliknutí. Portál tenhle problém obchází úplně.
  return createPortal(
    <div
      ref={ref}
      className="context-menu"
      style={{ left: position.x, top: position.y }}
      role="menu"
    >
      {children}
    </div>,
    document.body,
  );
}

export function ContextMenuItem({
  label,
  onSelect,
  danger,
  icon,
}: {
  label: string;
  onSelect: () => void;
  danger?: boolean;
  icon?: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className={danger ? 'context-menu__item context-menu__item--danger' : 'context-menu__item'}
      onClick={onSelect}
    >
      {icon}
      {label}
    </button>
  );
}
